/**
 * LOGOS Voice Agent — ElevenLabs Conversational AI Integration
 * 
 * Orbe flotante que conecta al usuario con ΛOGOS, un agente de consciencia
 * que lee y modifica el estado de la app en tiempo real via Client Tools.
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useConversation } from '@elevenlabs/react';
import { LOGOS_AGENT_CONFIG } from '../config/logosAgent';
import { COLORS } from '../domain';

export default function LogosVoiceAgent({
  sliderValues,
  metrics,
  verdict,
  activeTab,
  history,
  sessionHistory,
  user,
  accessToken,
  onUpdateSlider,
  onNavigateTab,
  onHighlightDomain,
  onRunSimulation,
}) {
  const { t } = useTranslation();
  const [isActive, setIsActive] = useState(false);
  const [agentMessage, setAgentMessage] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [textMode, setTextMode] = useState(false);
  const [highlightedDomain, setHighlightedDomain] = useState(null);
  const highlightTimer = useRef(null);

  const conversation = useConversation({
    clientTools: {
      // ═══ HERRAMIENTAS DE LECTURA ═══

      getCurrentDateTime: useCallback(async () => {
        const now = new Date();
        const locale = t('app.locale', { defaultValue: 'es-MX' });
        return JSON.stringify({
          date: now.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' }),
          time: now.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
          dayOfWeek: now.toLocaleDateString(locale, { weekday: 'long' }),
          month: now.toLocaleDateString(locale, { month: 'long' }),
          year: now.getFullYear(),
          iso: now.toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          timestamp: now.getTime(),
        });
      }, [t]),

      getFullState: useCallback(async () => {
        const labeled = {};
        for (const [key, val] of Object.entries(sliderValues || {})) {
          const label = LOGOS_AGENT_CONFIG.VARIABLE_LABELS[key] || key;
          labeled[key] = {
            label,
            value: typeof val === 'number' ? +val.toFixed(2) : val,
          };
        }
        return JSON.stringify(labeled);
      }, [sliderValues]),

      getMetrics: useCallback(async () => {
        return JSON.stringify({
          "Λ Lambda": metrics?.lambda?.toFixed(3),
          "Ω Omega": metrics?.omega?.toFixed(3),
          [`V ${t('misc.viability', { defaultValue: 'Viability' })}`]: metrics?.V?.toFixed(3),
          [`S ${t('misc.entropy', { defaultValue: 'Entropy' })}`]: metrics?.S?.toFixed(3),
          [`C ${t('misc.coherence', { defaultValue: 'Coherence' })}`]: metrics?.C?.toFixed(3),
          [`F ${t('misc.freeEnergy', { defaultValue: 'Free Energy' })}`]: metrics?.F?.toFixed(3),
          [`Q ${t('misc.trajectory', { defaultValue: 'Trajectory' })}`]: metrics?.Q?.toFixed(3),
        });
      }, [metrics, t]),

      getVerdict: useCallback(async () => {
        return JSON.stringify({
          verdict: verdict,
          lambda: metrics?.lambda?.toFixed(3),
          details: `V=${metrics?.V?.toFixed(2)}, S=${metrics?.S?.toFixed(2)}, C=${metrics?.C?.toFixed(2)}, Λ=${metrics?.lambda?.toFixed(2)}`,
        });
      }, [verdict, metrics]),

      getCurrentTab: useCallback(async () => {
        return JSON.stringify({ activeTab });
      }, [activeTab]),

      getTrajectory: useCallback(async () => {
        const recent = (history || []).slice(-10);
        const sessions = (sessionHistory || []).slice(-10);
        const trend = recent.length >= 2
          ? (recent[recent.length - 1]?.['Λ'] || 0) - (recent[0]?.['Λ'] || 0)
          : 0;
        return JSON.stringify({
          currentSession: recent,
          previousSessions: sessions,
          trend: trend > 0.05 ? t('voiceAgent.tool.trendUp') : trend < -0.05 ? t('voiceAgent.tool.trendDown') : t('voiceAgent.tool.trendStable'),
          trendDelta: +trend.toFixed(3),
          totalSessions: sessions.length,
        });
      }, [history, sessionHistory]),

      detectCrisis: useCallback(async () => {
        const sessions = sessionHistory || [];
        const recentSessions = sessions.slice(-5);
        const lambdaValues = recentSessions.map(s => s.lambda);
        const consecutiveLow = lambdaValues.filter(l => l < 0.20).length;
        const currentLambda = metrics?.lambda || 0;
        const isInCrisis = currentLambda < 0.20 && consecutiveLow >= 2;
        const isWarning = currentLambda < 0.30 && consecutiveLow >= 1;
        return JSON.stringify({
          status: isInCrisis ? 'CRISIS_A_MINUS' : isWarning ? 'WARNING' : 'STABLE',
          currentLambda: +currentLambda.toFixed(3),
          consecutiveLowSessions: consecutiveLow,
          recentLambdas: lambdaValues.map(l => +l.toFixed(3)),
          recommendation: isInCrisis
            ? t('voiceAgent.tool.crisisRec')
            : isWarning
            ? t('voiceAgent.tool.warningRec')
            : t('voiceAgent.tool.stableRec'),
        });
      }, [sessionHistory, metrics]),

      // ═══ HERRAMIENTAS DE ESCRITURA ═══

      setSliderValue: useCallback(async ({ variable, value }) => {
        const normalizedVar = variable?.toLowerCase()?.trim()
          ?.normalize("NFD")?.replace(/[\u0300-\u036f]/g, "");
        const internalKey = LOGOS_AGENT_CONFIG.SPANISH_TO_KEY[normalizedVar] || normalizedVar;
        const numValue = parseFloat(value);

        // Validate key exists in VARIABLE_LABELS to prevent writing to invalid state keys
        if (!(internalKey in LOGOS_AGENT_CONFIG.VARIABLE_LABELS)) {
          const validKeys = Object.values(LOGOS_AGENT_CONFIG.VARIABLE_LABELS).join(', ');
          return t('voiceAgent.tool.invalidKey', {
            defaultValue: `Error: Variable "${variable}" (resolved: "${internalKey}") is not valid. Valid variables: ${validKeys}`,
            variable, key: internalKey, validKeys,
          });
        }

        if (internalKey && !isNaN(numValue) && numValue >= 0 && numValue <= 1) {
          onUpdateSlider(internalKey, numValue);
          const label = LOGOS_AGENT_CONFIG.VARIABLE_LABELS[internalKey] || internalKey;
          return t('voiceAgent.tool.updated', { label, value: numValue.toFixed(2) });
        }
        return t('voiceAgent.tool.invalidVar', { variable, key: internalKey, value });
      }, [onUpdateSlider]),

      navigateToTab: useCallback(async ({ tab }) => {
        const normalizedTab = tab?.toLowerCase()?.trim();
        const mappedTab = LOGOS_AGENT_CONFIG.TAB_MAP[normalizedTab] || normalizedTab;
        onNavigateTab(mappedTab);
        return t('voiceAgent.tool.navigated', { tab: mappedTab });
      }, [onNavigateTab]),

      highlightDomain: useCallback(async ({ domain }) => {
        const normalizedDomain = domain?.toLowerCase()?.trim()
          ?.normalize("NFD")?.replace(/[\u0300-\u036f]/g, "");
        const mappedDomain = LOGOS_AGENT_CONFIG.DOMAIN_MAP[normalizedDomain] || normalizedDomain;
        onHighlightDomain(mappedDomain);
        return t('voiceAgent.tool.highlighted', { domain: mappedDomain });
      }, [onHighlightDomain]),

      triggerSimulation: useCallback(async () => {
        onRunSimulation();
        return t('voiceAgent.tool.simRun');
      }, [onRunSimulation]),

      // ═══ HERRAMIENTA DE EVALUACIÓN ═══

      evaluateDecision: useCallback(async ({ decision }) => {
        const lambda = metrics?.lambda || 0;
        const v = metrics?.V || 0;
        const s = metrics?.S || 0;
        const c = metrics?.C || 0;
        const readiness = (v > 0.55 && s < 0.40 && c > 0.45 && lambda > 0.45) ? 'READY' : 'NOT_READY';
        return JSON.stringify({
          decision,
          verdict,
          readiness,
          metrics: { lambda: +lambda.toFixed(3), V: +v.toFixed(3), S: +s.toFixed(3), C: +c.toFixed(3) },
          analysis: readiness === 'READY'
            ? t('voiceAgent.tool.ready')
            : t('voiceAgent.tool.notReady'),
        });
      }, [metrics, verdict]),

      // ═══ NIVEL 6 — FUNCIONALIDADES AVANZADAS ═══

      scheduleCalendarEvent: useCallback(async ({ title, description, dateTime, durationMinutes }) => {
        const token = accessToken || sessionStorage.getItem('logos-google-token');
        if (!token) {
          return JSON.stringify({ success: false, error: t('voiceAgent.tool.noGoogleToken') });
        }
        try {
          const start = new Date(dateTime);
          const end = new Date(start.getTime() + (parseInt(durationMinutes) || 15) * 60000);
          const event = {
            summary: title || t('voiceAgent.tool.defaultEventTitle'),
            description: description || t('voiceAgent.tool.defaultEventDesc'),
            start: { dateTime: start.toISOString(), timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
            end: { dateTime: end.toISOString(), timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
            reminders: { useDefault: false, overrides: [{ method: 'popup', minutes: 10 }] },
          };
          const resp = await fetch('https://www.googleapis.com/calendar/v3/calendars/primary/events', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(event),
          });
          if (!resp.ok) {
            const err = await resp.json().catch(() => ({}));
            return JSON.stringify({ success: false, error: err.error?.message || `HTTP ${resp.status}` });
          }
          const created = await resp.json();
          return JSON.stringify({
            success: true,
            eventId: created.id,
            htmlLink: created.htmlLink,
            summary: created.summary,
            start: created.start?.dateTime,
            end: created.end?.dateTime,
          });
        } catch (err) {
          return JSON.stringify({ success: false, error: err.message });
        }
      }, [accessToken]),

      getSessionReport: useCallback(async () => {
        const sessions = sessionHistory || [];
        if (sessions.length === 0) {
          return JSON.stringify({ error: t('voiceAgent.tool.noSessions') });
        }
        const dayMap = {};
        const weekdays = t('session.dayNames', { returnObjects: true }) || ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        let totalLambda = 0;
        sessions.forEach(s => {
          const d = new Date(s.date);
          const dayName = weekdays[d.getDay()];
          if (!dayMap[dayName]) dayMap[dayName] = [];
          dayMap[dayName].push(s.lambda);
          totalLambda += s.lambda;
        });
        const dayAverages = {};
        let worstDay = null, bestDay = null, worstAvg = 1, bestAvg = 0;
        for (const [day, lambdas] of Object.entries(dayMap)) {
          const avg = lambdas.reduce((a, b) => a + b, 0) / lambdas.length;
          dayAverages[day] = +avg.toFixed(3);
          if (avg < worstAvg) { worstAvg = avg; worstDay = day; }
          if (avg > bestAvg) { bestAvg = avg; bestDay = day; }
        }
        const avgLambda = totalLambda / sessions.length;
        const recent5 = sessions.slice(-5).map(s => +s.lambda.toFixed(3));
        const first5 = sessions.slice(0, 5).map(s => +s.lambda.toFixed(3));
        const earlyAvg = first5.reduce((a, b) => a + b, 0) / first5.length;
        const lateAvg = recent5.reduce((a, b) => a + b, 0) / recent5.length;
        return JSON.stringify({
          totalSessions: sessions.length,
          averageLambda: +avgLambda.toFixed(3),
          trend: lateAvg > earlyAvg + 0.03 ? t('voiceAgent.tool.improving') : lateAvg < earlyAvg - 0.03 ? t('voiceAgent.tool.declining') : t('voiceAgent.tool.trendStable'),
          dayOfWeekAverages: dayAverages,
          bestDay: { day: bestDay, avgLambda: +bestAvg.toFixed(3) },
          worstDay: { day: worstDay, avgLambda: +worstAvg.toFixed(3) },
          recentLambdas: recent5,
          userName: user?.name || t('voiceAgent.tool.defaultUser'),
        });
      }, [sessionHistory, user, t]),

      generateWeeklyReport: useCallback(async () => {
        const sessions = sessionHistory || [];
        const now = new Date();
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const weekSessions = sessions.filter(s => new Date(s.date) >= oneWeekAgo);
        if (weekSessions.length === 0) {
          return JSON.stringify({ error: t('voiceAgent.tool.noWeekData') });
        }
        const avgLambda = weekSessions.reduce((a, s) => a + s.lambda, 0) / weekSessions.length;
        const avgOmega = weekSessions.reduce((a, s) => a + s.omega, 0) / weekSessions.length;
        const avgV = weekSessions.reduce((a, s) => a + s.V, 0) / weekSessions.length;
        const avgS = weekSessions.reduce((a, s) => a + s.S, 0) / weekSessions.length;
        const avgC = weekSessions.reduce((a, s) => a + s.C, 0) / weekSessions.length;
        const verdicts = weekSessions.map(s => s.verdict).filter(Boolean);
        const verdictCounts = {};
        verdicts.forEach(v => { verdictCounts[v] = (verdictCounts[v] || 0) + 1; });
        const dominantVerdict = Object.entries(verdictCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';
        const lambdaTrend = weekSessions.length >= 2
          ? weekSessions[weekSessions.length - 1].lambda - weekSessions[0].lambda
          : 0;
        const prevWeekSessions = sessions.filter(s => {
          const d = new Date(s.date);
          return d >= new Date(oneWeekAgo.getTime() - 7 * 24 * 60 * 60 * 1000) && d < oneWeekAgo;
        });
        const prevAvgLambda = prevWeekSessions.length > 0
          ? prevWeekSessions.reduce((a, s) => a + s.lambda, 0) / prevWeekSessions.length
          : null;
        return JSON.stringify({
          period: `${oneWeekAgo.toLocaleDateString()} — ${now.toLocaleDateString()}`,
          sessionsThisWeek: weekSessions.length,
          averages: {
            lambda: +avgLambda.toFixed(3),
            omega: +avgOmega.toFixed(3),
            viability: +avgV.toFixed(3),
            entropy: +avgS.toFixed(3),
            coherence: +avgC.toFixed(3),
          },
          weekOverWeekChange: prevAvgLambda !== null ? +(avgLambda - prevAvgLambda).toFixed(3) : t('voiceAgent.tool.noPrevData'),
          lambdaTrend: lambdaTrend > 0.03 ? t('voiceAgent.tool.trendUp') : lambdaTrend < -0.03 ? t('voiceAgent.tool.trendDown') : t('voiceAgent.tool.trendStable'),
          dominantVerdict,
          verdictDistribution: verdictCounts,
          userName: user?.name || t('voiceAgent.tool.defaultUser'),
          userEmail: user?.email || null,
        });
      }, [sessionHistory, user, t]),

      getAlimentoRecommendation: useCallback(async () => {
        try {
          const { getAlimentoRecommendations } = await import('../core');
          const recs = getAlimentoRecommendations(sliderValues);
          const isEn = t('app.locale', { defaultValue: 'es-MX' }).startsWith('en');
          return JSON.stringify({
            deficientDimensions: recs.deficientDimensions.map(d => ({
              dimension: d.dimension,
              value: `${(d.value * 100).toFixed(0)}%`,
            })),
            recommendedFoods: recs.recommendedFoods.slice(0, 3).map(r => ({
              name: isEn ? r.food.name : r.food.nameEs,
              reason: isEn ? r.food.sacredNote : r.food.sacredNoteEs,
              predictedChange: `+${(r.predictedChange * 100).toFixed(1)}%`,
            })),
            avoidFoods: isEn ? recs.avoidFoods : recs.avoidFoodsEs,
            generalAdvice: isEn ? recs.generalAdvice : recs.generalAdviceEs,
          });
        } catch (err) {
          return JSON.stringify({ error: err.message });
        }
      }, [sliderValues, t]),
    },

    onConnect: () => {
      console.log('[ΛOGOS] Agente conectado ✓');
      setIsActive(true);
    },

    onDisconnect: (details) => {
      console.warn('[ΛOGOS] Agente desconectado', JSON.stringify(details, null, 2));
      setIsActive(false);
      setAgentMessage('');
      setIsSpeaking(false);
    },

    onMessage: (message) => {
      console.log('[ΛOGOS] Message:', message?.source, message?.message?.substring(0, 80));
      if (message?.source === 'ai') {
        setAgentMessage(message.message || '');
      }
    },

    onModeChange: (mode) => {
      console.log('[ΛOGOS] Mode:', mode?.mode);
      setIsSpeaking(mode?.mode === 'speaking');
    },

    onError: (error, context) => {
      console.error('[ΛOGOS] Error:', error, context);
      setIsActive(false);
    },
  });

  const toggleConversation = useCallback(async () => {
    try {
      if (conversation.status === 'connected') {
        await conversation.endSession();
      } else {
        const now = new Date();
        const locale = t('app.locale', { defaultValue: 'es-MX' });
        const dateStr = now.toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
        const timeStr = now.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const defaultUser = t('voiceAgent.tool.defaultUser', { defaultValue: 'User' });
        const userName = user?.user_metadata?.full_name || user?.email || defaultUser;
        const dateContext = locale.startsWith('en')
          ? `[TEMPORAL CONTEXT] Today is ${dateStr}, it's ${timeStr} (${tz}). The user's name is ${userName}.`
          : `[CONTEXTO TEMPORAL] Hoy es ${dateStr}, son las ${timeStr} (${tz}). El usuario se llama ${userName}.`;

        console.log('[ΛOGOS] Starting session, agentId:', LOGOS_AGENT_CONFIG.agentId, 'textMode:', textMode);
        // Request mic permission first
        try {
          await navigator.mediaDevices.getUserMedia({ audio: true });
          console.log('[ΛOGOS] Microphone permission granted ✓');
        } catch (micErr) {
          console.error('[ΛOGOS] Microphone permission denied:', micErr.message);
        }
        await conversation.startSession({
          agentId: LOGOS_AGENT_CONFIG.agentId,
          connectionType: 'webrtc',
          dynamicVariables: {
            date_context: dateContext,
            user_name: userName,
            lambda: metrics?.lambda?.toFixed(3) || '0.500',
            verdict: verdict || 'N/A',
          },
          ...(textMode ? { overrides: { conversation: { textOnly: true } } } : {}),
        });
        console.log('[ΛOGOS] Session started, status:', conversation.status);
      }
    } catch (err) {
      console.error('[ΛOGOS] Toggle error:', err);
    }
  }, [conversation, textMode, user]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (highlightTimer.current) clearTimeout(highlightTimer.current);
    };
  }, []);

  // ═══ ORB VISUAL LOGIC ═══
  const lambda = metrics?.lambda || 0;

  const getOrbColor = () => {
    if (lambda > 0.60) return COLORS.gold;
    if (lambda > 0.45) return COLORS.logos;
    if (lambda > 0.20) return COLORS.freeEnergy;
    return COLORS.entropy;
  };

  const orbColor = getOrbColor();
  const glowIntensity = 10 + lambda * 30;

  return (
    <>
      {/* ═══ BOTÓN ORB FLOTANTE ═══ */}
      <button
        onClick={toggleConversation}
        aria-label={isActive ? t('voiceAgent.stopAgent') : t('voiceAgent.talkAgent')}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: 64,
          height: 64,
          borderRadius: '50%',
          border: `2px solid ${orbColor}`,
          background: isActive
            ? `radial-gradient(circle, ${orbColor}44, ${COLORS.bg})`
            : COLORS.bg,
          boxShadow: `0 0 ${glowIntensity}px ${orbColor}66, 0 0 ${glowIntensity * 2}px ${orbColor}33`,
          cursor: 'pointer',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.5s ease',
          animation: isActive ? 'logosAgentBreath 3s ease-in-out infinite' : 'none',
          padding: 0,
          outline: 'none',
        }}
        title={isActive ? t('voiceAgent.stopAgent') : t('voiceAgent.talkAgent')}
      >
        <span style={{
          fontSize: 28,
          fontFamily: "'Palatino Linotype', Georgia, serif",
          color: orbColor,
          fontWeight: 'bold',
          textShadow: `0 0 10px ${orbColor}88`,
          lineHeight: 1,
        }}>
          Λ
        </span>
      </button>

      {/* ═══ TEXT MODE TOGGLE (when not active) ═══ */}
      {!isActive && (
        <button
          onClick={() => setTextMode(prev => !prev)}
          aria-label={textMode ? t('voiceAgent.switchToVoice') : t('voiceAgent.switchToText')}
          style={{
            position: 'fixed',
            bottom: 28,
            right: 96,
            width: 32,
            height: 32,
            borderRadius: '50%',
            border: `1px solid ${textMode ? COLORS.logos : COLORS.border}`,
            background: textMode ? `${COLORS.logos}20` : COLORS.bg,
            cursor: 'pointer',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            outline: 'none',
            transition: 'all 0.3s ease',
            fontSize: 14,
          }}
          title={textMode ? t('voiceAgent.textModeActive') : t('voiceAgent.voiceModeActive')}
        >
          <span style={{ color: textMode ? COLORS.logos : COLORS.textMuted }}>
            {textMode ? '⌨' : '🎙'}
          </span>
        </button>
      )}

      {/* ═══ INDICADOR DE ESTADO ═══ */}
      {isActive && (
        <div style={{
          position: 'fixed',
          bottom: 96,
          right: 24,
          background: `${COLORS.bg}ee`,
          border: `1px solid ${COLORS.logos}40`,
          borderRadius: 16,
          padding: '12px 16px',
          maxWidth: 300,
          minWidth: 200,
          zIndex: 9998,
          backdropFilter: 'blur(10px)',
          animation: 'fadeInUp 0.3s ease',
        }}>
          {/* Status indicator */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: agentMessage ? 8 : 0,
            fontSize: 11,
            fontFamily: "'JetBrains Mono', monospace",
            color: COLORS.logos,
            letterSpacing: 1,
          }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: isSpeaking ? COLORS.gold : COLORS.viability,
              animation: 'logosAgentBreath 1.5s ease-in-out infinite',
              flexShrink: 0,
            }} />
            {textMode ? t('voiceAgent.statusText') : isSpeaking ? t('voiceAgent.statusSpeaking') : t('voiceAgent.statusListening')}
          </div>

          {/* Subtítulos del agente */}
          {agentMessage && (
            <div style={{
              fontSize: 12,
              color: COLORS.textDim,
              lineHeight: 1.5,
              fontStyle: 'italic',
              maxHeight: 100,
              overflow: 'auto',
              borderTop: `1px solid ${COLORS.border}`,
              paddingTop: 8,
            }}>
              "{agentMessage}"
            </div>
          )}
        </div>
      )}

      {/* ═══ CSS ANIMATION ═══ */}
      <style>{`
        @keyframes logosAgentBreath {
          0%, 100% { transform: scale(1); opacity: 0.9; }
          50% { transform: scale(1.05); opacity: 1; }
        }
      `}</style>
    </>
  );
}
